import {
	findRaisedRegEdRound,
	getRegEdConditionsPending,
	IRegEdSubmissionMetadata,
	IRegEdSubmissionView,
	RegEdComplianceContentType,
	useAccountUsesRegEd,
	useRegEdBlogRegenerateCompliancePdfMutation,
	useRegEdRevisionFiling,
	useRegEdBlogReleaseMutation,
	useRegEdBlogReviseMutation,
} from '@Queries/RegEdCompliance';
import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { inject, observer } from 'mobx-react';
import moment from 'moment';
import * as React from 'react';
import { toast } from 'sonner';
import { IImpersonationContextComponentProps, ImpersonationContextKey } from '../../../../models';
import { CampaignType } from '../../../../models/AdminModels';
import { useEventLogging } from '../../../../models/Logging';
import { useErrorMessages, useFullscreenModal, useUserSession } from '../../../../models/hooks/appStateHooks';
import { postNotification } from '../../../../models/LocalNotifications';
import { Topics } from '../../../../models/LocalNotificationTopics';
import { accountHasComplianceEnabled, createRichContentEditorStateWithTextForBlog } from '../../../../models/UiUtils';
import {
	invalidateBlogPostDrafts,
	useApproveBlogPostMutation,
	useBlogSendComplianceMutation,
	useBlogUpdateMutation,
	useDeleteBlogPostMutation,
	useRejectBlogPostMutation,
} from '../../../../queries';
import { Button } from '../../../components-v2/Button';
import {
	Dialog,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogPopup,
	DialogTitle,
} from '../../../components-v2/Dialog';
import {
	ConfirmationDialog,
	DefaultDeleteConfirmationOptions,
	IConfirmationDialogOption,
} from '../../../components/ConfirmationDialog';
import {
	ApprovalStatus,
	ComplianceSendActions,
	ScheduleBy,
	ScheduleDate,
} from '../../../components/campaigns/CampaignDetailsSummary/presentation';
import { CampaignDateTimePicker } from '../../../components/campaigns/CampaignDateTimePicker';
import { ComplianceApprovalPrompt } from '../../../components/campaigns/ComplianceApprovalPrompt';
import { RegEdComplianceApprovalPrompt } from '../../../components/campaigns/ComplianceApprovalPrompt/RegEdComplianceApprovalPrompt';
import { RegEdComplianceStatus } from '../../../components/compliance/RegEdComplianceStatus';
import { RegEdReviseDateNotice } from '../../../components/compliance/RegEdReviseDateNotice';
import { RegEdRevisionOwedCard } from '../../../components/compliance/RegEdRevisionOwedCard';
import { IModalProps } from '../../../components/Modal';
import { WarningIcon } from '../../../components/svgs/icons/WarningIcon';
import { green3 } from '../../../styles/colors';
import { bs } from '../../../styles/styles';
import { BlogPostEditor } from './BlogPostEditor';
import { BlogPostPreviewAction } from './BlogPostPreviewAction';
import { BlogPostEditorContext, REDUCER_ACTION_KEYS, useBlogPostEditingRequest, validateContent } from './hooks';
import { IBlogPostComponentProps } from './models';
import { BlogPostEditorLayout } from './presentation';
import { styleSheet } from './styles';

const RejectionConfirmationOptions: IConfirmationDialogOption<boolean>[] = [
	{
		isDestructive: true,
		representedObject: true,
		title: 'Reject',
	},
	{
		isCancel: true,
		representedObject: false,
		title: 'Cancel',
	},
];

const detailsSidebarCardStyles = [styleSheet.detailsSidebarCard];

interface IEditBlogPostProps extends IBlogPostComponentProps, IImpersonationContextComponentProps {
	/** return true to indicate the action is handled externally */
	onPostApproved?(blogPost: Api.IBlogPost): boolean;
	/** return true to indicate the action is handled externally */
	onPostRejected?(blogPost: Api.IBlogPost): boolean;
}

function EditBlogPostBase({
	impersonationContext,
	onPostApproved,
	onPostRejected,
	onScheduleClicked,
}: IEditBlogPostProps) {
	const fullscreenModal = useFullscreenModal();
	const errorMessages = useErrorMessages();
	const userSession = useUserSession();
	const [isEditorLoaded, setIsEditorLoaded] = React.useState(false);
	const [isUnsavedChangesDialogOpen, setIsUnsavedChangesDialogOpen] = React.useState(false);
	const requiresCompliance = accountHasComplianceEnabled(userSession, impersonationContext);
	const usesRegEd = useAccountUsesRegEd(impersonationContext?.toJs());

	const {
		postEditorContext,
		blogPost: post,
		setBlogPost,
		setRegEdMetadata,
		regEdMetadata: savedRegEdMetadata,
	} = useBlogPostEditingRequest({
		impersonationContext,
	});

	const { logApiError } = useEventLogging('EditBlogPost');
	const [isReviseDialogOpen, setIsReviseDialogOpen] = React.useState(false);
	const [isRevising, setIsRevising] = React.useState(false);
	// Nothing on the response says the open round went unfiled, so the retry offer is held here for
	// the advisor who is still on the screen.
	// What a revision still owes, so the retry resumes at the step that failed rather than re-running
	// the last one — which would render a second PDF against an unrepointed submission and report success.
	// A compliance round outlives the date it was scheduled against, and approval publishes a past-dated
	// blog immediately rather than on its date, so the revision is the moment to move it.
	const [reviseSendDate, setReviseSendDate] = React.useState<Date | null>(null);
	const [pickingReviseDate, setPickingReviseDate] = React.useState(false);
	// Held here because the picker unmounts the prompt, which seeds only once per mount.
	const [reviseDraft, setReviseDraft] = React.useState<IRegEdSubmissionMetadata | null>(null);
	const [pendingRevision, setPendingRevision] = React.useState<IRegEdSubmissionView | null>(null);
	/**
	 * The revise routes declare `requiresUser`, which is a route constraint — an account-only context
	 * omits `impUserId` and the route simply does not match, giving a 404 rather than an error that
	 * says why. Matches what the email and social hosts do.
	 */
	const reviseImpersonationContext = () =>
		Api.ImpersonationBroker.withForUser(impersonationContext?.toJs(), post?.creator?.id);

	const regEdBlogRevise = useRegEdBlogReviseMutation({
		onError: error => errorMessages.pushApiError(error),
	});
	const regEdBlogRegeneratePdf = useRegEdBlogRegenerateCompliancePdfMutation({
		onError: error => errorMessages.pushApiError(error),
	});
	const { isRetryingRevision, onRetryRevisionClicked, revisionOwed, runRevisionFiling } = useRegEdRevisionFiling({
		getImpersonationContext: reviseImpersonationContext,
		logApiError,
		parentId: post?.id,
		parentType: RegEdComplianceContentType.Blog,
		regenerateCompliancePdf: (impersonation?: Api.IImpersonationContext) =>
			regEdBlogRegeneratePdf.mutateAsync({ blogPostId: post.id, impersonationContext: impersonation }),
		userSession,
	});

	const regEdBlogRelease = useRegEdBlogReleaseMutation({
		onError: error => errorMessages.pushApiError(error),
		onSuccess: released => {
			setBlogPost(released);
			dispatch({ type: REDUCER_ACTION_KEYS.SET_STATE, payload: { status: released.status } });
			if (released.status === Api.BlogStatus.Completed) {
				toast.warning('Released, but its scheduled date has passed — set a new date to publish this blog.');
			} else {
				toast.success('Released — this blog will publish as scheduled.');
			}
		},
	});
	const { state, dispatch } = postEditorContext;
	const stateRef = React.useRef<typeof state>(state);
	stateRef.current = state;
	const hasUnsavedChanges =
		!!post &&
		(state.content.getRawRichTextContent().document !== (post.content?.document ?? '') ||
			state.selectedImage?.id !== post.mainImage?.id ||
			state.title !== (post.title ?? ''));

	const rejectMutation = useRejectBlogPostMutation({
		impersonationContext: impersonationContext?.toJs(),
		onSuccess: val => {
			postNotification({
				info: val,
				topic: Topics.EDIT_BLOG_POST,
			});

			if (onPostRejected?.(val)) {
				return;
			}
			fullscreenModal.dismissModal();
		},
		onError: error => {
			errorMessages.pushApiError(error);
		},
	});

	const [rejectionConfirmationDialogModalProps, setShowRejectionConfirmationDialog] = React.useState<
		IModalProps<IConfirmationDialogOption<boolean>>
	>(() => {
		return {
			isOpen: false,
			onRequestClose: (result, canceled) => {
				setShowRejectionConfirmationDialog(v => ({
					...v,
					isOpen: false,
				}));
				if (!canceled && result?.isDestructive && stateRef.current?.blogId) {
					rejectMutation.mutate({ id: stateRef.current.blogId });
				}
			},
		};
	});

	const approveMutation = useApproveBlogPostMutation({
		impersonationContext: impersonationContext?.toJs(),
		onSuccess: val => {
			postNotification({
				info: val,
				topic: Topics.EDIT_BLOG_POST,
			});

			if (onPostApproved?.(val)) {
				return;
			}
			fullscreenModal.history.push({
				pathname: '/blog/post/success',
			});
		},
		onError: error => {
			errorMessages.pushApiError(error);
		},
	});

	const updateBlogPostMutation = useBlogUpdateMutation({
		impersonationContext: impersonationContext?.toJs(),
		onSuccess: blog => {
			setBlogPost(blog);
			toast.success('Blog post saved successfully');
			if (!impersonationContext?.isValid) {
				invalidateBlogPostDrafts({ userId: userSession.user.id });
			}
			postNotification({
				info: blog,
				topic: Topics.EDIT_BLOG_POST,
			});
		},
		onError: error => {
			errorMessages.pushApiError(error);
		},
	});

	const deleteBlogPostMutation = useDeleteBlogPostMutation({
		impersonationContext: impersonationContext?.toJs(),
		onSuccess: val => {
			postNotification({
				info: val,
				topic: Topics.EDIT_BLOG_POST,
			});
			fullscreenModal.dismissModal();
		},
		onError: error => {
			errorMessages.pushApiError(error);
		},
	});

	const sendComplianceMutation = useBlogSendComplianceMutation({
		impersonationContext: impersonationContext?.toJs(),
		onError: error => {
			toast.error('Failed to send copy');
			errorMessages.pushApiError(error);
		},
	});

	React.useEffect(() => {
		if (post) {
			const _content = post.content.document.replace(/<\/?lev-content>/gim, '');

			dispatch({
				type: REDUCER_ACTION_KEYS.SET_STATE,
				payload: {
					title: post.title,
					content: createRichContentEditorStateWithTextForBlog(_content),
					blogId: post.id,
					selectedImage: post.mainImage,
					blogGenerated: true,
					sendFromUser: post.creator as Api.IUser,
					status: post.status,
					scheduledSendDate: post.scheduledSendDate ? new Date(post.scheduledSendDate) : new Date(),
				},
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	let statusDescription = '';
	// convert to switch statement based on stat.status
	switch (state.status) {
		case Api.BlogStatus.Pending:
			statusDescription = 'Pending Approval';
			break;
		case Api.BlogStatus.Completed:
			statusDescription = 'Published';
			break;
		default:
			statusDescription = 'Draft';
			break;
	}

	// Re-send the pending blog's compliance PDF. `complianceEmail` is an explicit recipient, or null to let
	// the API route to its default (the impersonated client, or the logged-in user). Mirrors email/social.
	const handleSendCompliance = (complianceEmail: string | null) => {
		if (!state.blogId) {
			return;
		}
		sendComplianceMutation.mutate(
			{
				id: state.blogId,
				sendWithComplianceEmail: complianceEmail ?? undefined,
			},
			{
				onSuccess: () => {
					toast.success(`Copy sent to ${complianceEmail ? complianceEmail : "client's email address"}`);
				},
			}
		);
	};

	const getPostToSave = (overrides: Partial<Api.IBlogPost> = {}): Api.IBlogPost => ({
		...post,
		content: state.content.getRawRichTextContent(),
		title: state.title,
		mainImage: state.selectedImage,
		id: state.blogId,
		templateId: post?.templateId,
		sendFromUserId: state.sendFromUser?.id,
		...overrides,
	});

	/**
	 * Four calls whose order the API does not enforce: save the body (`revise` snapshots it, so an
	 * unsaved edit files what the reviewer already turned down), reopen the round, repoint the RegEd
	 * submission onto that round, then render the PDF — which is what files.
	 * `RegEdCompliancePdfGeneratedHandler` resolves the submission by the new round's id and gives up
	 * if it is missing, so skipping the repoint submits nothing and says so to no one.
	 */
	const onReviseConfirmed = async (submission: IRegEdSubmissionView) => {
		setIsRevising(true);
		try {
			// No success check, unlike email: this is a react-query mutation over `callAsync`, which throws
			// on failure, so a failed save rejects here and aborts before `revise` — and the mutation's own
			// onError surfaces it. Email needs the extra guard because `updateCampaign` resolves with no
			// value while the composer is busy.
			// The date rides along — getPostToSave takes overrides — so it needs no call of its own.
			await updateBlogPostMutation.mutateAsync({
				post: getPostToSave(reviseSendDate ? { scheduledSendDate: reviseSendDate.toISOString() } : {}),
			});
			const revised = await regEdBlogRevise.mutateAsync({
				blogPostId: post.id,
				impersonationContext: reviseImpersonationContext(),
			});
			// The container's copy still predates the raised round; leaving it stale lets the next save
			// write back approvalRequests without it.
			setBlogPost(revised);
			// The save above invalidated the drafts list before revise changed the status, so it refetched
			// the pre-revision state; this is the invalidation that reflects the revision.
			if (!impersonationContext?.isValid) {
				invalidateBlogPostDrafts({ userId: userSession.user.id });
			}
			// The reducer seeds on mount only, so the sidebar would keep showing the pre-revision status and
			// date until the editor is reopened.
			dispatch({
				type: REDUCER_ACTION_KEYS.SET_STATE,
				payload: {
					status: revised?.status,
					...(revised?.scheduledSendDate ? { scheduledSendDate: new Date(revised.scheduledSendDate) } : {}),
				},
			});
			const raisedRound = findRaisedRegEdRound(revised?.approvalRequests);
			if (!raisedRound?.id) {
				// Nothing to file against. Returning here rather than parking on the retry card, which would
				// replay the same empty call and never succeed.
				logApiError('RegEdBlogRevise-NoRaisedRound');
				toast.error(
					'The review was reopened, but we could not tell which round to file against. Reopen the blog post and try again.'
				);
				return;
			}
			await runRevisionFiling(raisedRound.id, submission, true);
		} catch (error) {
			logApiError('RegEdBlogRevise-Error', Api.asApiError(error));
		} finally {
			setIsRevising(false);
		}
	};

	const handleReviseCompliance = (submission: IRegEdSubmissionView) => {
		if (!post) {
			return;
		}
		// The dialog does not stay mounted, so a draft the advisor walked away from must not outrank the
		// round's real filing on the next open.
		setReviseDraft(null);
		setPendingRevision(submission);
		setReviseSendDate(state.scheduledSendDate ? new Date(state.scheduledSendDate) : null);
		setPickingReviseDate(false);
		setIsReviseDialogOpen(true);
	};

	const handleScheduleClick = (
		sendWithCompliance = false,
		sendWithComplianceEmail?: string,
		regEdMetadata?: IRegEdSubmissionMetadata
	) => {
		const errors = validateContent(state);
		if (errors.length !== 0) {
			errorMessages.push({
				messages: errors,
			});
			return;
		}

		const postToSave = getPostToSave({ sendWithCompliance, sendWithComplianceEmail });

		setRegEdMetadata(regEdMetadata);
		setBlogPost(postToSave); // context
		if (onScheduleClicked?.(postToSave)) {
			return;
		}
		fullscreenModal.history.push({
			pathname: '/blog/post/schedule',
		});
	};

	const approveBlogPost = () => {
		approveMutation.mutate({ id: state.blogId });
	};

	const handleApproveBlogPost = () => {
		if (hasUnsavedChanges) {
			setIsUnsavedChangesDialogOpen(true);
			return;
		}
		approveBlogPost();
	};

	// Shared so the approve action can sit inside the compliance card footer (matching the email/social
	// layout) when compliance applies, or in the standalone footer below when it does not.
	const approveBlogPostButton = (
		<Button
			disabled={updateBlogPostMutation.isPending || approveMutation.isPending || rejectMutation.isPending}
			isLoading={updateBlogPostMutation.isPending || approveMutation.isPending}
			onClick={handleApproveBlogPost}
		>
			Approve Blog Post
		</Button>
	);

	const saveBlogPost = (onSuccess?: () => void) => {
		updateBlogPostMutation.mutate({ post: getPostToSave() }, { onSuccess });
	};

	const handleSaveClick = () => {
		saveBlogPost();
	};

	const handleSaveAndApprove = () => {
		saveBlogPost(() => {
			setIsUnsavedChangesDialogOpen(false);
			approveBlogPost();
		});
	};

	const handleApproveWithoutSaving = () => {
		setIsUnsavedChangesDialogOpen(false);
		approveBlogPost();
	};

	const handleDeleteClick = () => {
		dispatch({ type: REDUCER_ACTION_KEYS.DELETE_BLOG_POST, payload: { value: true } });
	};
	const onDeleteConfirmationRequestClose = React.useCallback(
		(result?: IConfirmationDialogOption<boolean>, cancel?: boolean) => {
			if (result?.isDestructive && !cancel) {
				dispatch({ type: REDUCER_ACTION_KEYS.DELETE_BLOG_POST, payload: { value: false } });
				deleteBlogPostMutation.mutate({ id: state.blogId });
				return;
			}
			dispatch({ type: REDUCER_ACTION_KEYS.DELETE_BLOG_POST, payload: { value: false } });
		},
		[deleteBlogPostMutation, dispatch, state.blogId]
	);

	return (
		<BlogPostEditorContext.Provider value={postEditorContext}>
			<BlogPostEditorLayout
				status={state.status}
				header='Edit Blog'
				subHeader={
					<>
						<h1>Edit Blog</h1>
						<div className={css(styleSheet.menu)}>
							{impersonationContext?.isValid ? null : (
								<Button variant='secondary' size='sm' onClick={handleSaveClick} disabled={!isEditorLoaded}>
									Save Blog
								</Button>
							)}
							<BlogPostPreviewAction impersonationContext={impersonationContext} />
							<Button variant='destructive-secondary' size='sm' onClick={handleDeleteClick}>
								Delete
							</Button>
						</div>
					</>
				}
				sidebar={
					<>
						{post.status === Api.BlogStatus.Draft ? (
							requiresCompliance && state.status === Api.BlogStatus.Draft ? (
								<ComplianceApprovalPrompt
									campaignType={CampaignType.Blog}
									styles={[styleSheet.regEdComplianceScroll]}
									regEdInitialFormat={savedRegEdMetadata?.format}
									regEdInitialAnswers={savedRegEdMetadata?.answers}
									onScheduleCompliance={(e: React.MouseEvent<HTMLElement>, sendWithComplianceEmail: string) => {
										e.preventDefault();
										handleScheduleClick(true, sendWithComplianceEmail);
									}}
									onScheduleRegEdCompliance={(e, metadata) => {
										e.preventDefault();
										handleScheduleClick(true, undefined, metadata);
									}}
									onScheduleWithoutCompliance={() => handleScheduleClick()}
								/>
							) : (
								<>
									<>
										<h2 className={css(styleSheet.question)}>What would you like to do with this blog?</h2>
										<button type='button' className={css(bs.ctaButton)} onClick={() => handleScheduleClick()}>
											Schedule
										</button>
									</>
								</>
							)
						) : null}
						{post?.actor ? <ScheduleBy name={`${post.actor!.name}`} cardStyles={detailsSidebarCardStyles} /> : null}
						{state.status !== Api.BlogStatus.Draft ? (
							<ScheduleDate
								scheduleDate={`${moment(state.scheduledSendDate).utc()}`}
								title='Scheduled Date'
								showChangeButton={true}
								disabled={false}
								onClick={() => handleScheduleClick()}
								cardStyles={detailsSidebarCardStyles}
							/>
						) : null}
						{post?.id && post.status !== Api.BlogStatus.Draft ? (
							<RegEdComplianceStatus
								parentId={post.id}
								impersonationContext={impersonationContext?.toJs()}
								onRevise={handleReviseCompliance}
								conditionsPending={getRegEdConditionsPending(post.approvalRequests)}
								onReleaseConditions={() =>
									regEdBlogRelease.mutate({ blogPostId: post.id, impersonationContext: impersonationContext?.toJs() })
								}
								isReleasing={regEdBlogRelease.isPending}
								isRevising={isRevising}
								reviseDisabled={!!revisionOwed}
								styles={[styleSheet.regEdComplianceCard]}
							/>
						) : null}
						{revisionOwed ? (
							<RegEdRevisionOwedCard
								isRetrying={isRetryingRevision}
								needsFiling={revisionOwed.needsFiling}
								onRetry={onRetryRevisionClicked}
							/>
						) : null}
						{/* Completed compliance posts are already published, so they should fall through to the Published status. */}
						{state.status === Api.BlogStatus.Pending && requiresCompliance && !usesRegEd ? (
							<ComplianceSendActions
								isImpersonating={!!impersonationContext?.isValid}
								selfEmail={userSession.user.email}
								disabled={sendComplianceMutation.isPending}
								onSend={handleSendCompliance}
								cardStyles={detailsSidebarCardStyles}
								// Approve lives inside the card footer (like the email/social "Mark as Approved"); the
								// blog has no "publish without compliance" action, so there is no "Send Anyway" button.
								footer={
									!impersonationContext?.isValid ? (
										<div className={css(styleSheet.complianceApproveFooter)}>{approveBlogPostButton}</div>
									) : null
								}
							/>
						) : state.status === Api.BlogStatus.Completed || (state.status === Api.BlogStatus.Pending && !usesRegEd) ? (
							<ApprovalStatus
								title={statusDescription}
								textColor={state.status === Api.BlogStatus.Completed ? green3 : undefined}
								cardStyles={detailsSidebarCardStyles}
							/>
						) : null}
						<Dialog
							open={isReviseDialogOpen}
							onOpenChange={open => {
								if (!isRevising) {
									setIsReviseDialogOpen(open);
								}
							}}
						>
							<DialogPopup showCloseButton={!isRevising}>
								<DialogHeader>
									<DialogTitle>Revise and resubmit for compliance review</DialogTitle>
									<DialogDescription>
										Your edits are saved and the reviewer sees the blog post again. Update the compliance details if the
										reviewer asked you to.
									</DialogDescription>
								</DialogHeader>
								{pickingReviseDate ? (
									<CampaignDateTimePicker
										ctaText='Select'
										// Not an email: the non-sending-day and send-interval rules are email's, and applying
										// them here would refuse the off-hours date the piece was already scheduled for.
										ignoreDisabledDays
										ignoreEmailTimeIntervals
										initialValue={reviseSendDate ?? new Date()}
										onCancel={() => setPickingReviseDate(false)}
										onSave={date => {
											setReviseSendDate(date);
											setPickingReviseDate(false);
										}}
									/>
								) : (
									<>
										{reviseSendDate ? (
											<>
												{/* No sidebar card styles here: they cap the width at the sidebar's, which leaves the
												    card narrower than the questionnaire beside it in the dialog. */}
												<ScheduleDate
													disabled={isRevising}
													onClick={() => setPickingReviseDate(true)}
													scheduleDate={reviseSendDate.toISOString()}
													showChangeButton={true}
													title='Publish Date'
												/>
												<RegEdReviseDateNotice date={reviseSendDate} kind='blog' />
											</>
										) : null}
										{/* Editable, not replayed: a reviewer can reject because of the form, and the requested
								    review date inside the answers goes stale between rounds. */}
										<RegEdComplianceApprovalPrompt
											campaignType={CampaignType.Blog}
											impersonationContext={impersonationContext?.toJs()}
											initialAnswers={reviseDraft?.answers ?? pendingRevision?.metadata?.answers}
											initialFormat={reviseDraft?.format ?? pendingRevision?.metadata?.format}
											// A blog may be dated in the past, so only the review date is derived; first use stays the advisor's.
											isRevision
											revisionSendDate={null}
											onFormStateChange={setReviseDraft}
											isLoading={isRevising}
											onScheduleRegEdCompliance={async (_, metadata) => {
												if (!pendingRevision) {
													return;
												}
												try {
													await onReviseConfirmed({ ...pendingRevision, metadata });
												} finally {
													// Held open for the duration so the in-flight guards above actually hold the advisor.
													setIsReviseDialogOpen(false);
												}
											}}
											styles={[styleSheet.reviseDialogForm]}
											submitLabel='Revise & Resubmit'
										/>
									</>
								)}
							</DialogPopup>
						</Dialog>
						<footer className={css(styleSheet.footer)}>
							{state.status === Api.BlogStatus.Pending && !impersonationContext?.isValid ? (
								<>
									{/* When compliance applies, approve moved into the compliance card footer above. */}
									{requiresCompliance ? null : approveBlogPostButton}

									<Button
										variant='destructive-secondary'
										disabled={rejectMutation.isPending}
										onClick={() => {
											setShowRejectionConfirmationDialog(v => ({
												...v,
												isOpen: true,
											}));
										}}
									>
										Reject blog post
									</Button>

									<ConfirmationDialog
										icon={<WarningIcon />}
										modalProps={rejectionConfirmationDialogModalProps}
										options={RejectionConfirmationOptions}
										title='Are you sure you want to reject this blog post?'
									/>
								</>
							) : null}
						</footer>
						<ConfirmationDialog
							icon={<WarningIcon />}
							modalProps={{
								isOpen: state.showConfirmationModal,
								onRequestClose: onDeleteConfirmationRequestClose,
							}}
							options={DefaultDeleteConfirmationOptions}
							title='Are you sure you want to delete this blog post?'
						/>
						<Dialog
							open={isUnsavedChangesDialogOpen}
							onOpenChange={open => {
								if (!updateBlogPostMutation.isPending) {
									setIsUnsavedChangesDialogOpen(open);
								}
							}}
							dismissOnOutsideClick={!updateBlogPostMutation.isPending}
						>
							<DialogPopup showCloseButton={!updateBlogPostMutation.isPending}>
								<DialogHeader>
									<DialogTitle>Save changes before approving?</DialogTitle>
									<DialogDescription>
										This blog post has unsaved changes. Save and approve to keep them, or approve without saving to
										discard them.
									</DialogDescription>
								</DialogHeader>
								<DialogFooter>
									<Button
										variant='secondary'
										onClick={() => setIsUnsavedChangesDialogOpen(false)}
										disabled={updateBlogPostMutation.isPending}
									>
										Cancel
									</Button>
									<Button
										variant='destructive-secondary'
										onClick={handleApproveWithoutSaving}
										disabled={updateBlogPostMutation.isPending}
									>
										Approve Without Saving
									</Button>
									<Button onClick={handleSaveAndApprove} isLoading={updateBlogPostMutation.isPending}>
										Save and Approve
									</Button>
								</DialogFooter>
							</DialogPopup>
						</Dialog>
					</>
				}
			>
				<BlogPostEditor onEditorLoad={() => setIsEditorLoaded(true)} />
			</BlogPostEditorLayout>
		</BlogPostEditorContext.Provider>
	);
}

export const EditBlogPost = inject(ImpersonationContextKey)(observer(EditBlogPostBase));
